import React, { useState } from "react";
import Button from "../../../shared/Button";
import Modal from "../../../shared/Modal";
import TextArea from "../../../shared/TextArea";
import { useUpdateManifestV2Mutation } from "../../../redux/services/manifests/manifestsV2Api";
import { STATUS_ACTIONS } from "../utils";
import { toast } from "react-toastify";

/**
 * Status transition actions with optional compliance override.
 *
 * @param {object} props
 * @param {object} props.manifest
 * @param {boolean} [props.canEdit]
 */
const ManifestV2StatusActions = ({ manifest, canEdit = false }) => {
  const [updateManifestV2, { isLoading }] = useUpdateManifestV2Mutation();
  const [overrideModal, setOverrideModal] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");

  function formatGateFailureMessage(error, fallbackMessage) {
    const evaluation = error?.data?.details?.evaluation;
    const blocks = evaluation?.blocks || [];

    if (!blocks.length) {
      return fallbackMessage;
    }

    const summaries = blocks
      .map((block) => block.message || block.code)
      .filter(Boolean);

    return `${fallbackMessage}: ${summaries.join("; ")}`;
  }

  const actions = STATUS_ACTIONS[manifest?.status] || [];

  if (!canEdit || !actions.length) {
    return null;
  }

  async function applyStatus(nextStatus, gate, withOverride = false) {
    const body = { status: nextStatus };
    if (withOverride && gate) {
      body.complianceOverride = { gate, reason: overrideReason.trim() };
    }

    try {
      await updateManifestV2({ id: manifest._id, body }).unwrap();
      toast.success(`Manifest updated to ${nextStatus.replace("_", " ")}`);
      setOverrideModal(null);
      setOverrideReason("");
    } catch (error) {
      const message = error?.data?.message || "Status update failed";
      if (gate && !withOverride) {
        setOverrideModal({
          nextStatus,
          gate,
          message: formatGateFailureMessage(error, message),
          evaluation: error?.data?.details?.evaluation,
        });
        return;
      }
      toast.error(message);
    }
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {actions.map((action) => (
          <Button
            key={action.status}
            type="primary"
            size="sm"
            loading={isLoading}
            onClick={() => applyStatus(action.status, action.gate)}
          >
            {action.label}
          </Button>
        ))}
      </div>

      <Modal
        visible={Boolean(overrideModal)}
        setVisible={() => setOverrideModal(null)}
        title="Compliance override required"
        footer={
          <div className="flex gap-2">
            <Button type="alternative" onClick={() => setOverrideModal(null)}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={isLoading}
              disabled={!overrideReason.trim()}
              onClick={() =>
                applyStatus(
                  overrideModal.nextStatus,
                  overrideModal.gate,
                  true
                )
              }
            >
              Override and continue
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-gray-600">
          {overrideModal?.message ||
            "This transition is blocked by compliance rules. Provide a reason to override."}
        </p>
        <TextArea
          label="Override reason"
          value={overrideReason}
          onChange={(e) => setOverrideReason(e.target.value)}
          rows={3}
        />
      </Modal>
    </>
  );
};

export default ManifestV2StatusActions;
